import { useLocation } from 'react-router-dom';
import {
  LifeBuoy,
  Headphones,
  ArrowRight,
  CheckCircle2,
  Sparkles
} from 'lucide-react';
import SupportSubmission from './SupportSubmission';
import { DarkModeToggle } from "@/components/theme/DarkModeToggle";

const SupportPage = () => {
  const location = useLocation();
  const state = (location.state as { traceId?: string; message?: string } | null) || {};
  const searchParams = new URLSearchParams(location.search);
  const traceId = state.traceId || searchParams.get('traceId') || undefined;

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-[#eff5ff] via-[#f6fbff] to-white px-4 py-10 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-20 h-64 w-64 rounded-full bg-blue-200/40 blur-[120px]" />
        <div className="absolute top-1/3 -left-10 h-72 w-72 rounded-full bg-teal-200/35 blur-[140px]" />
      </div>

      <div className="relative mx-auto w-full">
        <div className="rounded-[32px] border border-white/60 bg-white/85 shadow-[0_30px_160px_rgba(15,23,42,0.08)] backdrop-blur-2xl dark:border-white/10 dark:bg-slate-900/70">
          {/* Top Navigation */}
          <div className="flex flex-col gap-4 border-b border-white/70 px-6 py-6 md:flex-row md:items-center md:justify-between dark:border-white/10">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-500 text-white">
                <LifeBuoy className="h-7 w-7" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-slate-400">Campus Support Desk</p>
                <h1 className="text-3xl font-semibold text-slate-900 md:text-[2.4rem] dark:text-white">Need help running your school cloud?</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">Dedicated success team for portals, finance, timetables & guardian comms.</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-3 rounded-2xl border border-slate-200/60 bg-white/70 px-4 py-2 text-sm font-medium text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-200">
                <Sparkles className="h-4 w-4 text-blue-500" />
                Avg. response &lt; 90 mins
              </div>
              <DarkModeToggle />
            </div>
          </div>

          <div className="grid gap-10 px-6 py-8 md:px-10 lg:grid-cols-[1.1fr_0.9fr]">
            {/* Left Column */}
            <div className="space-y-8">
              <div className="space-y-5">
                <div className="flex flex-wrap gap-3">
                  {['Onboarding', 'Attendance & exams', 'Fees & finance', 'Integrations'].map((chip, idx) => (
                    <button
                      key={chip}
                      type="button"
                      className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${idx === 0 ? 'bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900' : 'border-slate-200/70 text-slate-500 hover:border-slate-400 dark:border-white/10 dark:text-slate-300'}`}
                    >
                      {chip}
                    </button>
                  ))}
                </div>
                <p className="text-lg text-slate-600 dark:text-slate-300">
                  Managing enrolment, boarding, or district reporting? Share the context and our campus success squad will get your admins, teachers, and guardians back on track fast.
                </p>
                {traceId && (
                  <div className="inline-flex items-center gap-2 rounded-full border border-dashed border-blue-300/70 bg-blue-50/70 px-4 py-1 text-xs font-semibold text-blue-600 dark:border-blue-400/40 dark:bg-blue-500/10 dark:text-blue-200">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Ref: {traceId}
                  </div>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {[
                  { title: 'Attendance automations', desc: 'Configure biometric or QR logs that sync with term reports.' },
                  { title: 'Fee workflows', desc: 'Map flexible fee items, waivers, and MPESA reconciliation.' },
                  { title: 'Exam publishing', desc: 'Release grades to guardians with moderation & ranking rules.' },
                  { title: 'Staff onboarding', desc: 'Provision teacher accounts, classrooms, and subject rights in minutes.' }
                ].map((item) => (
                  <div key={item.title} className="rounded-2xl border border-slate-200/70 bg-white/90 p-4 shadow-sm transition hover:-translate-y-1 hover:shadow-lg dark:border-white/5 dark:bg-white/5">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">{item.title}</p>
                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">{item.desc}</p>
                    <button className="mt-4 inline-flex items-center text-sm font-semibold text-blue-600">
                      Learn more
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column (Form) */}
            <div className="space-y-5">
              <div className="rounded-3xl border border-slate-200/80 bg-gradient-to-br from-white via-white to-blue-50 p-6 shadow-xl dark:border-white/5 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900">
                <div className="mb-6 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600/10 text-blue-600 dark:bg-blue-500/10">
                    <Headphones className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Submit ticket</p>
                    <p className="text-2xl font-semibold text-slate-900 dark:text-white">Tell us what’s blocking learning</p>
                  </div>
                </div>
                <SupportSubmission traceId={traceId} hideHeader={true} />
              </div>
              <div className="rounded-2xl border border-dashed border-slate-200/70 bg-white/70 p-4 text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                Most timetable, gradebook, and finance sync issues close within 2 school hours once we receive screenshots, CSV exports, or guardian complaints for context.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
